<script lang="ts">
  let {
    value = $bindable(),
    options = [],
    placeholder = 'Select...',
    onkeydown = undefined,
    onCreate = undefined
  }: {
    value?: string;
    options: string[];
    placeholder?: string;
    onkeydown?: (e: KeyboardEvent) => void;
    onCreate?: (newValue: string) => Promise<void> | void;
  } = $props();

  let isOpen = $state(false);
  let inputRef: HTMLInputElement;
  let dropdownRef: HTMLDivElement;
  let inputValue = $state(value || '');
  let isCreating = $state(false);
  let openUpwards = $state(false);

  // Update inputValue when value prop changes
  $effect(() => {
    if (!isOpen) {
      inputValue = value || '';
    }
  });

  const filteredOptions = $derived(
    options.filter(option =>
      option.toLowerCase().includes((inputValue || '').toLowerCase())
    )
  );

  let dropdownPosition = $state({ top: 0, left: 0, width: 0 });

  function handleInputFocus() {
    isOpen = true;
    inputValue = '';

    // Calculate dropdown position relative to viewport
    if (inputRef) {
      const rect = inputRef.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300; // max-height of dropdown

      // Open upwards if not enough space below but more space above
      openUpwards = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

      // Store position for fixed positioning
      dropdownPosition = {
        top: openUpwards ? rect.top : rect.bottom,
        left: rect.left,
        width: rect.width
      };
    }
  }

  function handleInputBlur(e: FocusEvent) {
    // Don't close if clicking inside the dropdown
    if (dropdownRef && dropdownRef.contains(e.relatedTarget as Node)) {
      return;
    }
    setTimeout(() => {
      isOpen = false;
      inputValue = value;
    }, 150);
  }

  function selectOption(option: string) {
    value = option;
    inputValue = option;
    isOpen = false;
    inputRef?.blur();
  }

  async function handleCreate() {
    if (!onCreate || !inputValue.trim() || isCreating) return;

    isCreating = true;
    try {
      await onCreate(inputValue.trim());
      value = inputValue.trim();
      isOpen = false;
      inputRef?.blur();
    } catch (error) {
      console.error('Error creating category:', error);
    } finally {
      isCreating = false;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      isOpen = false;
      inputValue = value;
      inputRef?.blur();
    }
    if (onkeydown) {
      onkeydown(e);
    }
  }
</script>

<div class="searchable-select">
  <input
    bind:this={inputRef}
    type="text"
    bind:value={inputValue}
    {placeholder}
    onfocus={handleInputFocus}
    onblur={handleInputBlur}
    onkeydown={handleKeydown}
    autocomplete="off"
  />

  {#if isOpen && filteredOptions.length > 0}
    <div
      class="dropdown"
      class:open-upwards={openUpwards}
      bind:this={dropdownRef}
      style="top: {dropdownPosition.top}px; left: {dropdownPosition.left}px; width: {dropdownPosition.width}px;"
    >
      {#each filteredOptions as option}
        <button
          type="button"
          class="dropdown-item"
          class:selected={option === value}
          onclick={() => selectOption(option)}
          tabindex="0"
        >
          {option}
        </button>
      {/each}
    </div>
  {/if}

  {#if isOpen && filteredOptions.length === 0 && inputValue.trim()}
    <div
      class="dropdown"
      class:open-upwards={openUpwards}
      bind:this={dropdownRef}
      style="top: {dropdownPosition.top}px; left: {dropdownPosition.left}px; width: {dropdownPosition.width}px;"
    >
      {#if onCreate}
        <button
          type="button"
          class="dropdown-item create-option"
          onclick={handleCreate}
          disabled={isCreating}
          tabindex="0"
        >
          {#if isCreating}
            Creating...
          {:else}
            ✨ Create category: "{inputValue.trim()}"
          {/if}
        </button>
      {:else}
        <div class="no-results">No matches found</div>
      {/if}
    </div>
  {/if}
</div>

<style lang="scss">
  .searchable-select {
    position: relative;
    width: 100%;
  }

  input {
    width: 100%;
    padding: var(--spacing-sm);
    border: 1px solid var(--color-border-medium);
    border-radius: var(--spacing-xs);
    font-size: var(--font-size-sm);

    &:focus {
      outline: none;
      border-color: var(--color-primary-bright);
    }
  }

  .dropdown {
    position: fixed; /* Fixed positioning to escape modal overflow clipping */
    background: white;
    border: 1px solid var(--color-border-medium);
    border-radius: var(--spacing-xs);
    box-shadow: var(--shadow-lg);
    max-height: 300px;
    overflow-y: auto;
    z-index: 1100; /* Higher than modal (1000) to appear above modal content */

    &:not(.open-upwards) {
      margin-top: var(--spacing-xs);
      transform: translateY(0);
    }

    &.open-upwards {
      transform: translateY(-100%);
      margin-top: calc(var(--spacing-xs) * -1);
    }
  }

  .dropdown-item {
    width: 100%;
    padding: var(--spacing-sm) var(--spacing-md);
    text-align: left;
    border: none;
    background: white;
    cursor: pointer;
    font-size: var(--font-size-sm);
    transition: background-color 0.15s;

    &:hover {
      background: var(--color-background-tertiary);
    }

    &.selected {
      background: var(--color-primary-light);
      font-weight: var(--font-weight-medium);
    }

    &.create-option {
      color: var(--color-primary-bright);
      font-weight: var(--font-weight-medium);
      border-top: 1px solid var(--color-border);

      &:hover:not(:disabled) {
        background: var(--color-primary-light);
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }
    }

    &:first-child {
      border-radius: var(--radius-sm) 4px 0 0;
    }

    &:last-child {
      border-radius: 0 0 4px var(--spacing-xs);
    }
  }

  .no-results {
    padding: var(--spacing-md);
    text-align: center;
    color: var(--color-text-tertiary);
    font-size: var(--font-size-sm);
  }
</style>
